import { useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Upload, Download, FileUp, CheckCircle2, AlertTriangle } from "lucide-react";

export default function BulkUpload({ open, onOpenChange, endpoint, templateName, templateContent, onDone, testidPrefix }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = () => {
    const blob = new Blob([templateContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      if (res.data.created > 0) {
        toast.success(`${res.data.created} record(s) imported`);
        onDone?.();
      }
      if (res.data.failed > 0) toast.error(`${res.data.failed} row(s) failed`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="rounded-none max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tighter">
            Bulk Upload (CSV)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <button
            onClick={downloadTemplate}
            data-testid={`${testidPrefix}-download-template`}
            className="w-full flex items-center justify-center gap-2 bg-zinc-100 text-zinc-900 border border-zinc-300 rounded-none hover:bg-zinc-200 transition-colors px-4 py-2 text-sm font-bold"
          >
            <Download size={16} /> Download CSV template
          </button>

          <label
            className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-zinc-300 py-8 cursor-pointer hover:border-brand transition-colors"
            data-testid={`${testidPrefix}-dropzone`}
          >
            <FileUp size={28} className="text-zinc-400" />
            <span className="text-sm font-semibold text-zinc-700">
              {file ? file.name : "Click to select a .csv file"}
            </span>
            <input
              ref={inputRef}
              type="file"
              accept=".csv"
              className="hidden"
              data-testid={`${testidPrefix}-file-input`}
              onChange={(e) => {
                setFile(e.target.files[0] || null);
                setResult(null);
              }}
            />
          </label>

          <button
            onClick={upload}
            disabled={!file || uploading}
            data-testid={`${testidPrefix}-upload-button`}
            className="w-full flex items-center justify-center gap-2 bg-brand text-white rounded-full font-bold tracking-tight hover:bg-brand-dark transition-colors px-6 py-2.5 disabled:opacity-50"
          >
            <Upload size={16} /> {uploading ? "Uploading…" : "Import"}
          </button>

          {result && (
            <div className="border border-zinc-200 p-4 text-sm" data-testid={`${testidPrefix}-result`}>
              <div className="flex items-center gap-4 mb-2">
                <span className="flex items-center gap-1.5 font-bold text-emerald-700">
                  <CheckCircle2 size={16} /> {result.created} imported
                </span>
                {result.failed > 0 && (
                  <span className="flex items-center gap-1.5 font-bold text-red-700">
                    <AlertTriangle size={16} /> {result.failed} failed
                  </span>
                )}
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-red-700 max-h-40 overflow-y-auto">
                  {result.errors.map((er, i) => (
                    <li key={er.row ?? i}>
                      Row {er.row}: {er.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
